import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { lumi } from '../lib/lumi';
import {Link2, QrCode, BarChart3, Copy, ExternalLink} from 'lucide-react';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';

const PartnerDashboard: React.FC = () => {
  const { slug: partnerId } = useParams<{ slug: string }>();
  const [partner, setPartner] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');

  useEffect(() => {
    if (partnerId) {
      fetchPartner();
    }
  }, [partnerId]);

  const fetchPartner = async () => {
    try {
      const result = await lumi.entities.partners.list({
        filter: { _id: partnerId },
        limit: 1
      });
      
      if (result.list.length > 0) {
        const partnerData = result.list[0];
        setPartner(partnerData);
        generateQRCode(partnerData.partnerSlug);
      }
    } catch (error) {
      console.error('Failed to fetch partner:', error);
      toast.error('Failed to load partner information');
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async (slug: string) => {
    try {
      const url = `${window.location.origin}/p/${slug}`;
      const qrDataUrl = await QRCode.toDataURL(url, {
        width: 300,
        margin: 2,
        color: {
          dark: '#06243A',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(qrDataUrl);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/p/${partner.partnerSlug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `${partner.partnerSlug}-qr-code.png`;
    link.click();
    toast.success('QR code downloaded!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-900 via-navy-800 to-blue-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-cyan-400 border-t-transparent"></div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-900 via-navy-800 to-blue-900 flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-2xl font-bold mb-4">Partner not found</h1>
          <p className="text-gray-300">The partner page you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const partnerUrl = `${window.location.origin}/p/${partner.partnerSlug}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-900 via-navy-800 to-blue-900 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-4 mb-4">
            {partner.logoUrl && (
              <img 
                src={partner.logoUrl} 
                alt={partner.partnerName}
                className="h-16 w-16 object-contain rounded-lg bg-white p-2"
              />
            )}
            <div>
              <h1 className="text-3xl font-bold text-white">{partner.partnerName}</h1>
              <p className="text-gray-300">Partner Tools & Analytics</p>
            </div>
          </div>
          {partner.referralCode && (
            <div className="bg-white/10 rounded-lg p-4 inline-block">
              <p className="text-sm text-gray-300 mb-1">Your Referral Code</p>
              <p className="text-2xl font-bold text-cyan-400">{partner.referralCode}</p>
            </div>
          )}
        </div>

        {/* Analytics */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-6 h-6 text-cyan-400" />
              <h3 className="font-semibold text-white">Total Visits</h3>
            </div>
            <p className="text-4xl font-bold text-white">{partner.visits || 0}</p>
          </div>

          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <ExternalLink className="w-6 h-6 text-blue-400" />
              <h3 className="font-semibold text-white">CTA Clicks</h3>
            </div>
            <p className="text-4xl font-bold text-white">{partner.ctaClicks || 0}</p>
          </div>

          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-6 h-6 text-purple-400" />
              <h3 className="font-semibold text-white">Conversion Rate</h3>
            </div>
            <p className="text-4xl font-bold text-white">
              {partner.visits > 0 
                ? `${Math.round((partner.ctaClicks / partner.visits) * 100)}%`
                : '0%'
              }
            </p>
          </div>
        </div>

        {/* Landing Page Link */}
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Link2 className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-bold text-white">Your Landing Page</h2>
          </div>
          <div className="bg-white/5 rounded-lg p-4 mb-4">
            <p className="text-gray-300 text-sm mb-2">Share this link with your clients:</p>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={partnerUrl}
                readOnly
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white"
              />
              <button
                onClick={copyLink}
                className="flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-all"
              >
                <Copy size={18} />
                Copy
              </button>
            </div>
          </div>
          <a
            href={partnerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ExternalLink size={18} />
            View your landing page
          </a>
        </div>

        {/* QR Code */}
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <QrCode className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-bold text-white">QR Code</h2>
          </div>
          <div className="flex flex-col md:flex-row gap-8 items-center">
            {qrCodeUrl && (
              <div className="bg-white p-6 rounded-xl">
                <img src={qrCodeUrl} alt="QR Code" className="w-64 h-64" />
              </div>
            )}
            <div className="flex-1">
              <p className="text-gray-300 mb-6">
                Share this QR code with your clients. They can scan it with their phone to instantly access your partner landing page.
              </p>
              <button
                onClick={downloadQRCode}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold rounded-lg transition-all shadow-lg"
              >
                <QrCode size={18} />
                Download QR Code
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerDashboard;
